import moment from 'moment';
import { t } from 'i18next';
import { useEffect } from 'react';
import { Field, FormRenderProps, GridFlex, Text } from '@stigg-components';
import { ActiveSubscriptionsFragment, SubscriptionStatus } from '@stigg-types/apiTypes';
import { AdditionalInputLayout } from '.';
import { StartDateConfiguration, SubscriptionFormFields } from '../SubscriptionForm.types';
import { DEFAULT_SUBSCRIPTION_FORM_COMPONENT_WIDTH } from './consts';
import { useGetActiveSubscriptions } from '../../../../../queries/useGetActiveSubscriptions';

type UseActiveSubscriptionProps = {
  productId: string;
  customerId: string;
  resourceId: string;
};

type UseStartDateConfigurationChangedProps = {
  startDateConfiguration: StartDateConfiguration;
  activeSubscription?: ActiveSubscriptionsFragment;
  formRenderProps: FormRenderProps<SubscriptionFormFields>;
  isUpdatingSubscription: boolean;
};

type SubscriptionStartProps = {
  customerId: string;
  isUpdatingSubscription: boolean;
  formRenderProps: FormRenderProps<SubscriptionFormFields>;
};

function useActiveSubscription({ productId, customerId, resourceId }: UseActiveSubscriptionProps) {
  const { data: activeSubscriptions } = useGetActiveSubscriptions({ customerId, resourceId });

  const activeSubscription = activeSubscriptions?.find(
    (subscription) =>
      productId === subscription.plan.product.refId && subscription.status === SubscriptionStatus.Active,
  );

  return activeSubscription;
}

function useStartDateConfigurationChanged({
  startDateConfiguration,
  activeSubscription,
  formRenderProps,
  isUpdatingSubscription,
}: UseStartDateConfigurationChangedProps) {
  const { setFieldValue } = formRenderProps;

  useEffect(() => {
    if (isUpdatingSubscription) {
      return;
    }

    switch (startDateConfiguration) {
      case StartDateConfiguration.Now: {
        const startDate = moment.utc().toLocaleString();
        setFieldValue('startDate', startDate);
        break;
      }
      case StartDateConfiguration.EndOfBillingPeriod: {
        const startDate = moment.utc(activeSubscription?.currentBillingPeriodEnd || undefined).toLocaleString();
        setFieldValue('startDate', startDate);
        break;
      }
      case StartDateConfiguration.Custom: {
        const startDateMoment = moment.utc();
        if (activeSubscription?.currentBillingPeriodStart) {
          const otherDate = moment.utc(activeSubscription.currentBillingPeriodStart);
          startDateMoment.hours(otherDate.hours());
          startDateMoment.minutes(otherDate.minutes());
          startDateMoment.seconds(otherDate.seconds());
          startDateMoment.milliseconds(otherDate.milliseconds());
        }
        setFieldValue('startDate', startDateMoment.toLocaleString());
        break;
      }
    }
  }, [startDateConfiguration, activeSubscription, setFieldValue, isUpdatingSubscription]);
}

export function SubscriptionStart({ isUpdatingSubscription, formRenderProps, customerId }: SubscriptionStartProps) {
  const {
    values: { startDateConfiguration, resourceId, productId },
  } = formRenderProps;

  const activeSubscription = useActiveSubscription({ productId, customerId, resourceId });

  useStartDateConfigurationChanged({
    startDateConfiguration,
    activeSubscription,
    formRenderProps,
    isUpdatingSubscription,
  });

  const startDateConfigurationFields: Field<SubscriptionFormFields>[] = [
    {
      type: 'select',
      id: 'startDateConfiguration',
      values: [
        {
          displayValue: t('subscriptionForm.subscriptionStartDateConfiguration.immediately'),
          value: StartDateConfiguration.Now,
        },
        ...(activeSubscription?.currentBillingPeriodEnd
          ? [
              {
                displayValue: t('subscriptionForm.subscriptionStartDateConfiguration.endOfBillingPeriod'),
                value: StartDateConfiguration.EndOfBillingPeriod,
              },
            ]
          : []),
        {
          displayValue: t('subscriptionForm.subscriptionStartDateConfiguration.custom'),
          value: StartDateConfiguration.Custom,
        },
      ],
      restProps: {
        width: DEFAULT_SUBSCRIPTION_FORM_COMPONENT_WIDTH,
      },
    },
  ];
  const startDateFields: Field<SubscriptionFormFields>[] = [
    {
      type: 'datePicker',
      id: 'startDate',
      disabled: isUpdatingSubscription || startDateConfiguration === StartDateConfiguration.EndOfBillingPeriod,
      gridProps: {
        width: DEFAULT_SUBSCRIPTION_FORM_COMPONENT_WIDTH,
      },
    },
  ];

  return (
    <GridFlex.Column gap={2}>
      <Text.H6>{t('subscriptionForm.subscriptionScheduleSectionTitle')}</Text.H6>

      {!isUpdatingSubscription && (
        <AdditionalInputLayout
          isSet
          setIcon={{ icon: 'Calendar' }}
          setLabel={t('subscriptions.createSubscriptionStartDateFieldShort')}
          fields={startDateConfigurationFields}
          formRenderProps={formRenderProps}
        />
      )}

      {(startDateConfiguration !== StartDateConfiguration.Now || isUpdatingSubscription) && (
        <AdditionalInputLayout
          isSet
          setIcon={isUpdatingSubscription ? { icon: 'Calendar' } : undefined}
          setLabel={
            isUpdatingSubscription
              ? t('subscriptions.createSubscriptionStartDateField')
              : t('subscriptions.createSubscriptionSetStartDateField')
          }
          fields={startDateFields}
          formRenderProps={formRenderProps}
        />
      )}
    </GridFlex.Column>
  );
}
